/**
 * Invoice Detail Page - T103, T132
 */
import { useState, useEffect } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import {
  GlassPanel,
  Button,
  LoadingSpinner,
  EmptyState,
  Modal,
  PageHeader,
} from '../components/ui';
import { InvoiceStatusBadge } from '../components/invoices/InvoiceStatusBadge';
import { InvoiceItems } from '../components/invoices/InvoiceItems';
import { InvoiceTotals } from '../components/invoices/InvoiceTotals';
import { InvoiceActions } from '../components/invoices/InvoiceActions';
import { PaymentDateModal } from '../components/invoices/PaymentDateModal';
import { ReminderList } from '../components/reminders/ReminderList';
import { EntityAuditSection } from '../components/audit/EntityAuditSection';
import {
  useInvoice,
  useCancelInvoice,
  useDeleteInvoice,
  useUpdateInvoiceNotes,
  useUpdateInvoicePaymentDate,
} from '../hooks/useInvoices';
import { useToastHelpers } from '../components/ui/Toast';
import { formatCurrency, formatDate } from '@armard/shared';

export const Route = createFileRoute('/invoices_/$id')({
  component: InvoiceDetailPage,
});

function InvoiceDetailPage() {
  const params = Route.useParams();
  const id = params.id;
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const { data: invoice, isLoading, error } = useInvoice(id);
  const cancelInvoice = useCancelInvoice();
  const deleteInvoice = useDeleteInvoice();
  const updateNotes = useUpdateInvoiceNotes();
  const updatePaymentDate = useUpdateInvoicePaymentDate();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPaymentDateModal, setShowPaymentDateModal] = useState(false);
  const [internalNotes, setInternalNotes] = useState(invoice?.internalNotes ?? '');
  const [showOnPdf, setShowOnPdf] = useState(invoice?.showInternalNotesOnPdf ?? false);
  const [notesDirty, setNotesDirty] = useState(false);

  // Sync local state when invoice data loads
  useEffect(() => {
    if (invoice) {
      setInternalNotes(invoice.internalNotes ?? '');
      setShowOnPdf(invoice.showInternalNotesOnPdf ?? false);
      setNotesDirty(false);
    }
  }, [invoice?.internalNotes, invoice?.showInternalNotesOnPdf]);

  const handleSaveNotes = async () => {
    if (!invoice) return;
    try {
      await updateNotes.mutateAsync({
        id: invoice.id,
        internalNotes: internalNotes || null,
        showInternalNotesOnPdf: showOnPdf,
      });
      setNotesDirty(false);
      toast.success('Notizen gespeichert');
    } catch {
      toast.error('Fehler beim Speichern');
    }
  };

  const isOverdue = invoice?.status === 'sent' && new Date(invoice.dueDate) < new Date();
  const displayStatus = isOverdue ? 'overdue' : invoice?.status;

  if (isLoading) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  if (error || !invoice) {
    return (
      <ProtectedRoute>
        <AppShell>
          <EmptyState
            title="Rechnung nicht gefunden"
            description="Die angeforderte Rechnung existiert nicht oder wurde gelöscht."
            action={{
              label: 'Zurück zu Rechnungen',
              onClick: () => navigate({ to: '/invoices' }),
            }}
          />
        </AppShell>
      </ProtectedRoute>
    );
  }

  const handleCancel = async () => {
    try {
      await cancelInvoice.mutateAsync({
        id: invoice!.id,
        version: invoice!.version,
      });
      toast.success('Rechnung storniert');
      setShowCancelConfirm(false);
    } catch {
      toast.error('Fehler beim Stornieren');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteInvoice.mutateAsync(invoice!.id);
      toast.success('Rechnung gelöscht');
      navigate({ to: '/invoices' });
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleUpdatePaymentDate = async (paymentDate: string) => {
    try {
      await updatePaymentDate.mutateAsync({
        id: invoice.id,
        paymentDate,
        version: invoice.version,
      });
      toast.success('Bezahldatum aktualisiert');
      setShowPaymentDateModal(false);
    } catch {
      toast.error('Fehler beim Aktualisieren des Bezahldatums');
    }
  };

  const isDraft = invoice?.status === 'draft';
  const isSent = invoice?.status === 'sent';
  const isPaid = invoice?.status === 'paid';
  const isCancelled = invoice?.status === 'cancelled';

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header — 006b P1: shared PageHeader (backTo-Eyebrow-Kanon) */}
        <PageHeader
          backTo="/invoices"
          backLabel="Rechnungen"
          title={invoice.invoiceNumber}
          titleAccessory={displayStatus ? <InvoiceStatusBadge status={displayStatus} /> : undefined}
          actions={
            <>
              {isDraft && (
                <Button
                  variant="secondary"
                  className="text-red-400 hover:text-red-300"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Löschen
                </Button>
              )}
              {(isSent || isPaid || invoice?.status === 'deferred') && !isCancelled && (
                <Button
                  variant="secondary"
                  className="text-red-400 hover:text-red-300"
                  onClick={() => setShowCancelConfirm(true)}
                >
                  Stornieren
                </Button>
              )}
              {isDraft && (
                <Link to="/invoices/$id/edit" params={{ id: invoice.id }}>
                  <Button>Bearbeiten</Button>
                </Link>
              )}
            </>
          }
        />

        {/* A10: min-w-0 auf beiden Grid-Kindern — Grid-Children haben
            min-width:auto, dadurch clippte die rechte Aktions-/Kunden-Rail
            bei schmalen Desktop-Breiten statt zu schrumpfen. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 min-w-0">
            {/* Invoice Info */}
            <GlassPanel>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-white/50">Kunde</div>
                  <div className="text-white font-medium">
                    {invoice.customer?.companyName || 'Unbekannt'}
                  </div>
                  <div className="text-sm text-white/50">{invoice.customer?.customerNumber}</div>
                </div>
                <div>
                  <div className="text-sm text-white/50">Rechnungsdatum</div>
                  <div className="text-white">{formatDate(invoice.invoiceDate)}</div>
                </div>
                <div>
                  <div className="text-sm text-white/50">Fällig am</div>
                  <div className={isOverdue ? 'text-red-400' : 'text-white'}>
                    {formatDate(invoice.dueDate)}
                  </div>
                </div>
                {invoice.paymentDate && (
                  <div>
                    <div className="text-sm text-white/50">Bezahlt am</div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">{formatDate(invoice.paymentDate)}</span>
                      {isPaid && (
                        <button
                          type="button"
                          onClick={() => setShowPaymentDateModal(true)}
                          className="text-xs text-white/40 hover:text-white/70 transition-colors"
                        >
                          Ändern
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {invoice.customerReference && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="text-sm text-white/50">Kundenreferenz</div>
                  <div className="text-white">{invoice.customerReference}</div>
                </div>
              )}
            </GlassPanel>

            {/* Invoice Items */}
            <GlassPanel>
              <h3 className="text-lg font-medium text-white mb-4">Positionen</h3>

              {invoice.items.length === 0 ? (
                <div className="text-center py-8 text-white/40">Keine Positionen</div>
              ) : (
                <InvoiceItems items={invoice.items} readOnly />
              )}

              {/* Totals */}
              {invoice.items.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex justify-end">
                    <InvoiceTotals
                      overallDiscountPercent={parseFloat(invoice.overallDiscountPercent || '0')}
                      items={invoice.items}
                      netTotal={parseFloat(invoice.netTotal)}
                      vatRate={parseFloat(invoice.vatRate)}
                      vatAmount={parseFloat(invoice.vatAmount)}
                      grossTotal={parseFloat(invoice.grossTotal)}
                      className="w-80 max-w-full"
                    />
                  </div>
                </div>
              )}
            </GlassPanel>

            {/* Notes (PDF body text) */}
            {invoice.notes && (
              <GlassPanel>
                <h3 className="text-sm font-medium text-white/70 mb-2">Rechnungstext</h3>
                <p className="text-white/60 whitespace-pre-wrap">{invoice.notes}</p>
              </GlassPanel>
            )}

            {/* Internal Notes - always editable */}
            <GlassPanel>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-white/70">Interne Notizen</h3>
                {notesDirty && (
                  <button
                    onClick={handleSaveNotes}
                    disabled={updateNotes.isPending}
                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
                  >
                    {updateNotes.isPending ? 'Speichern...' : 'Speichern'}
                  </button>
                )}
              </div>
              <textarea
                value={internalNotes}
                onChange={(e) => {
                  setInternalNotes(e.target.value);
                  setNotesDirty(true);
                }}
                onBlur={() => {
                  if (notesDirty) handleSaveNotes();
                }}
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white placeholder:text-white/30 focus:outline-none focus:border-white/20 focus:bg-white/[0.05] transition-colors resize-none text-sm"
                placeholder="Interne Notizen hinzufügen..."
              />
              <label className="flex items-center gap-2 mt-2 text-xs text-white/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnPdf}
                  onChange={(e) => {
                    setShowOnPdf(e.target.checked);
                    setNotesDirty(true);
                  }}
                  className="w-3.5 h-3.5 rounded border-white/20 bg-white/5 accent-white/80"
                />
                Auf PDF anzeigen
              </label>
            </GlassPanel>

            {/* Payment Reminders (T132) */}
            {['sent', 'overdue', 'paid', 'deferred'].includes(invoice.status) && (
              <ReminderList
                invoiceId={invoice.id}
                invoiceGrossTotal={parseFloat(invoice.grossTotal)}
                invoiceStatus={invoice.status}
              />
            )}

            {/* Audit trail (Phase 13 Cluster A Feature D) */}
            <EntityAuditSection entityType="invoice" entityId={invoice.id} />

            {/* Cancellation Info */}
            {invoice.status === 'cancelled' && invoice.cancellationId && (
              <GlassPanel className="border-orange-500/20">
                <div className="flex items-center gap-2 text-orange-400">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span className="font-medium">Diese Rechnung wurde storniert</span>
                </div>
                <Link
                  to="/invoices/$id"
                  params={{ id: invoice.cancellationId }}
                  className="mt-2 inline-block text-sm text-orange-400 hover:text-orange-300"
                >
                  Gutschrift anzeigen →
                </Link>
              </GlassPanel>
            )}

            {/* Credit Note Reference */}
            {invoice.cancelledById && (
              <GlassPanel className="border-blue-500/20">
                <div className="flex items-center gap-2 text-blue-400">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="font-medium">Dies ist eine Gutschrift</span>
                </div>
                <Link
                  to="/invoices/$id"
                  params={{ id: invoice.cancelledById }}
                  className="mt-2 inline-block text-sm text-blue-400 hover:text-blue-300"
                >
                  Ursprüngliche Rechnung anzeigen →
                </Link>
              </GlassPanel>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4 min-w-0">
            <InvoiceActions invoice={invoice} />

            {/* Quick Stats */}
            <GlassPanel>
              <h3 className="text-sm font-medium text-white/70 mb-3">Übersicht</h3>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-white/50">Positionen</dt>
                  <dd className="text-white">{invoice.items.length}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-white/50">MwSt.-Satz</dt>
                  <dd className="text-white">{parseFloat(invoice.vatRate)}%</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-white/50">Gesamtbetrag</dt>
                  <dd className="text-white font-medium">
                    {formatCurrency(parseFloat(invoice.grossTotal))}
                  </dd>
                </div>
              </dl>
            </GlassPanel>

            {/* Customer Info */}
            {invoice.customer && (
              <GlassPanel>
                <h3 className="text-sm font-medium text-white/70 mb-3">Kundendaten</h3>
                <div className="space-y-2">
                  <div className="text-white font-medium">{invoice.customer.companyName}</div>
                  <div className="text-sm text-white/50">{invoice.customer.customerNumber}</div>
                  {invoice.customer.email && (
                    <a
                      href={`mailto:${invoice.customer.email}`}
                      className="text-sm text-blue-400 hover:text-blue-300 block"
                    >
                      {invoice.customer.email}
                    </a>
                  )}
                </div>
                <Link
                  to="/customers/$id"
                  params={{ id: invoice.customer.id }}
                  className="mt-3 text-sm text-white/50 hover:text-white/70 inline-flex items-center"
                >
                  Kunde anzeigen
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </GlassPanel>
            )}
          </div>
        </div>

        {/* 006b P8: Confirm-Dialoge → ui/Modal (Focus-Trap, ESC; Kanon §7) */}
        <Modal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Rechnung löschen?"
          size="sm"
        >
          <p className="text-white/60 mb-4">Diese Aktion kann nicht rückgängig gemacht werden.</p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
              Abbrechen
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              loading={deleteInvoice.isPending}
            >
              Löschen
            </Button>
          </div>
        </Modal>

        <Modal
          isOpen={showCancelConfirm}
          onClose={() => setShowCancelConfirm(false)}
          title="Rechnung stornieren?"
          size="sm"
        >
          <p className="text-white/60 mb-4">
            Es wird eine Gutschrift erstellt. Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowCancelConfirm(false)}>
              Abbrechen
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleCancel}
              loading={cancelInvoice.isPending}
            >
              Stornieren
            </Button>
          </div>
        </Modal>

        <PaymentDateModal
          isOpen={showPaymentDateModal}
          initialDate={invoice.paymentDate ?? invoice.invoiceDate}
          title="Bezahldatum ändern"
          confirmLabel="Datum speichern"
          isPending={updatePaymentDate.isPending}
          onClose={() => setShowPaymentDateModal(false)}
          onConfirm={handleUpdatePaymentDate}
        />
      </AppShell>
    </ProtectedRoute>
  );
}
